import { AddCircle, Info, RemoveCircle } from "@mui/icons-material";
import {
  Alert,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from "react-hook-form";
import { BulkUpdateFormValues } from "./BulkUpdateModal";
import TagsSelect from "../TagsSelect";
import ViewsPermissionWrapper from "../../../views/components/ViewsPermissionWrapper";
import ViewsSelect from "../../../views/components/ViewsSelect";

// RetentionDateTab.tsx
interface RetentionDateTabProps {
  control: Control<any>;
  errors: FieldErrors<BulkUpdateFormValues>;
  clearRetentionDate: boolean;
  setValue: UseFormSetValue<any>;
}

interface TagsTabProps {
  control: Control<any>;
  currentTagsToAdd: string[];
  currentTagsToRemove: string[];
  customerId: string | undefined;
}

interface ViewsTabProps {
  control: Control<any>;
  customerId: string | undefined;
  currentViewsToAdd: string[];
  currentViewsToRemove: string[];
  user: any;
}

/**
 * Displays retention date tab for bulk update
 * @param param0
 * @returns
 */
export function BulkUpdateRetentionTab({
  control,
  errors,
  clearRetentionDate,
  setValue,
}: Readonly<RetentionDateTabProps>) {
  return (
    <Stack spacing={0} mt={1}>
      <Controller
        name="retentionDate"
        control={control}
        render={({ field }) => (
          <DateTimePicker
            label="Retention Date"
            minDateTime={dayjs()}
            disabled={clearRetentionDate}
            slotProps={{
              textField: {
                error: !!errors.retentionDate,
                helperText: errors.retentionDate?.message,
              },
            }}
            value={
              field.value ? dayjs(new Date(Number(field.value) * 1000)) : null
            }
            views={["year", "month", "day", "hours", "minutes", "seconds"]}
            onChange={(newValue) => {
              field.onChange(newValue?.isValid() ? newValue.unix() : null);
            }}
          />
        )}
      />
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Controller
          name="removeRetention"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked);
                    setValue("retentionDate", null);
                  }}
                />
              }
              label="Remove retention date?"
            />
          )}
        />
        <Controller
          name="putOnHold"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              }
              label="Apply legal hold?"
            />
          )}
        />
      </Stack>
      <Alert severity="info" icon={<Info />}>
        Bulk removal of legal hold is not permitted.
      </Alert>
    </Stack>
  );
}

/**
 * Displays a tags to add/remove select for bulk edit modal
 * @returns
 */
export function BulkUpdateTagsTab({
  control,
  currentTagsToAdd,
  currentTagsToRemove,
  customerId,
}: Readonly<TagsTabProps>) {
  return (
    <Stack spacing={3}>
      <Controller
        name="tagsToAdd"
        control={control}
        render={({ field }) => (
          <TagsSelect
            value={field.value}
            currentTags={currentTagsToAdd}
            customerId={customerId}
            onChange={field.onChange}
            label="Add tags"
            icon={<AddCircle color="success" />}
          />
        )}
      />
      <Divider />
      <Controller
        name="tagsToRemove"
        control={control}
        render={({ field }) => (
          <TagsSelect
            value={field.value}
            currentTags={currentTagsToRemove}
            customerId={customerId}
            onChange={field.onChange}
            label="Remove tags"
            placeholder="Select tags..."
            freeSolo={false}
            icon={<RemoveCircle color="error" />}
          />
        )}
      />
    </Stack>
  );
}

export function BulkUpdateViewsTab({
  control,
  customerId,
  currentViewsToAdd,
  currentViewsToRemove,
  user,
}: Readonly<ViewsTabProps>) {
  return (
    <ViewsPermissionWrapper
      user={user}
      resource="views"
      requiredPermission="Update"
    >
      <Stack spacing={3}>
        <Controller
          name="viewsToAdd"
          control={control}
          render={(field) => (
            <ViewsSelect
              customerId={customerId}
              currentViews={currentViewsToAdd}
              onChange={(selectedViews) => field.field.onChange(selectedViews)}
              label="Add Views"
              placeholder="Select views..."
              icon={<AddCircle color="success" />}
            />
          )}
        />
        <Divider />
        <Controller
          name="viewsToRemove"
          control={control}
          render={(field) => (
            <ViewsSelect
              customerId={customerId}
              currentViews={currentViewsToRemove}
              onChange={(selectedViews) => field.field.onChange(selectedViews)}
              label="Remove Views"
              placeholder="Select views..."
              icon={<RemoveCircle color="error" />}
            />
          )}
        />
      </Stack>
    </ViewsPermissionWrapper>
  );
}
